import { useEffect, useRef } from 'react';

// Santiago's prizes, as the first half of a sentence the next section finishes. The slide is
// one airport departures board of large characters — each the size the amount is read at —
// in the landing's dotted title face. At first it lists flights out of SCL. When it comes into
// view every character starts to flip, a wave across the board, and lands: the cells of
// "PREMIOS DE $8,000 EN CASH + PASAJES A" in full ink, every other cell on some other letter in a dim
// one, so the prize reads out of the board like a word out of a word search. The arrow at
// the foot drops into GoldenGateSection, whose own board reads SAN FRANCISCO.
//
// Light: a large character a cell means a couple of hundred cells at most, built once per
// layout and flipped by hand (no React re-render per tick).

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const SPINNING = `${LETTERS}0123456789$+`;
// the heading over the amount, on line(s) of its own
const HEADER = ['PREMIOS', 'DE'];
const MESSAGE = ['$8,000', 'EN', 'CASH', '+', 'PASAJES'];
// the last word stands on its own, two rows under the rest and centred: the "a" the next
// section's SAN FRANCISCO completes
const LAST_WORD = 'A';
const LAST_WORD_DROP = 2;
const TICK_MS = 60;
const SPIN = 9; // flaps every cell turns through at least, once it starts
const ASPECT = 0.74; // a cell's width over its height

// Flights out of Santiago for the board's resting state (no San Francisco: that is the reveal).
const FLIGHTS = [
  'LA1412 LIMA 22:40',
  'AV0128 BOGOTA 22:55',
  'LA0624 CDMX 23:10',
  'AR1281 BUENOS AIRES 23:25',
  'LA0704 MADRID 23:40',
  'AA0940 MIAMI 23:55',
  'LA8180 SAO PAULO 00:10',
  'LA0530 NUEVA YORK 00:25',
  'IB6830 BARCELONA 00:40',
  'LA2370 CARACAS 00:55',
  'CM0498 PANAMA 01:10',
  'AF0401 PARIS 01:25',
  'LA0800 AUCKLAND 01:40',
  'LA2464 RAPA NUI 01:55',
  'LA2459 PUNTA ARENAS 02:10',
  'KL0702 AMSTERDAM 02:25',
  'LA0532 TORONTO 02:40',
  'UA0846 HOUSTON 02:55',
];

// hero: the amount, inverted (ink tiles, dark letters), the one thing on the board read first
type Tone = 'lit' | 'dim' | 'hero';
type Cell = { char: string; tone: Tone };
const AMOUNT = MESSAGE[0];
type Layout = { cols: number; rows: number; size: number; start: string[][]; end: Cell[][] };

const randomLetter = () => LETTERS[Math.floor(Math.random() * LETTERS.length)];

// Words wrapped to the board's width, greedily.
function wrap(words: string[], cols: number) {
  const lines: string[] = [];
  for (const word of words) {
    const last = lines[lines.length - 1];
    if (last !== undefined && last.length + 1 + word.length <= cols) lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  }
  return lines;
}

// The heading, then the message, each wrapped to the board's width, every line centred, the
// block centred on the board's rows.
function placeMessage(cols: number, rows: number) {
  // on a narrow board the amount gets a line to itself, centred, instead of sharing one
  // pushed to the edge with the word after it
  const narrow = cols < AMOUNT.length + 6;
  const body = narrow ? [AMOUNT, ...wrap(MESSAGE.slice(1), cols)] : wrap(MESSAGE, cols);
  const lines = [...wrap(HEADER, cols), ...body];
  const height = lines.length - 1 + LAST_WORD_DROP + 1;
  const top = Math.max(0, Math.floor((rows - height) / 2));
  const lit = new Map<string, Cell>(); // "r,c" → its cell
  const place = (line: string, r: number) => {
    const left = Math.floor((cols - line.length) / 2);
    // the amount is a word of its own on its line, never split by the wrap
    const at = ` ${line}`.indexOf(` ${AMOUNT}`);
    [...line].forEach((ch, j) => {
      const hero = at >= 0 && j >= at && j < at + AMOUNT.length;
      if (ch !== ' ') lit.set(`${r},${left + j}`, { char: ch, tone: hero ? 'hero' : 'lit' });
    });
  };
  lines.forEach((line, i) => place(line, top + i));
  place(LAST_WORD, Math.min(rows - 1, top + lines.length - 1 + LAST_WORD_DROP));
  return lit;
}

function buildLayout(width: number, height: number): Layout {
  const target = width >= 768 ? 76 : 40; // px a cell: the size the amount is read at
  // at least as many columns as the longest word of the message needs, plus a margin
  const longest = Math.max(...MESSAGE.map((w) => w.length));
  const cols = Math.max(longest + 2, Math.floor(width / target));
  const size = width / cols;
  const rows = Math.max(3, Math.floor(height / (size / ASPECT)));
  const lit = placeMessage(cols, rows);
  const start = Array.from({ length: rows }, (_, r) => {
    const line = FLIGHTS[r % FLIGHTS.length].slice(0, cols);
    return Array.from({ length: cols }, (_, c) => line[c] ?? ' ');
  });
  const end = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => {
      return lit.get(`${r},${c}`) ?? ({ char: randomLetter(), tone: 'dim' } satisfies Cell);
    }),
  );
  return { cols, rows, size, start, end };
}

export function SantiagoPrizeSlide() {
  const sectionRef = useRef<HTMLElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const frame = frameRef.current;
    const board = boardRef.current;
    if (!section || !frame || !board) return;
    let state: 'board' | 'flipping' | 'done' = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'done' : 'board';
    let layout: Layout | null = null;
    let cells: HTMLSpanElement[][] = [];
    let timer = 0;
    let delay = 0;

    const paint = (el: HTMLSpanElement, char: string, tone: 'board' | Tone) => {
      el.textContent = char === ' ' ? '' : char;
      el.dataset.tone = tone;
    };

    const build = () => {
      layout = buildLayout(frame.clientWidth, frame.clientHeight);
      const { cols, rows, size, start, end } = layout;
      board.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
      board.style.fontSize = `${size * 0.78}px`;
      board.replaceChildren();
      cells = [];
      for (let r = 0; r < rows; r++) {
        const row: HTMLSpanElement[] = [];
        for (let c = 0; c < cols; c++) {
          const el = document.createElement('span');
          el.className = 'board-cell';
          if (state === 'done') paint(el, end[r][c].char, end[r][c].tone);
          else paint(el, start[r][c], 'board');
          board.appendChild(el);
          row.push(el);
        }
        cells.push(row);
      }
    };

    const flip = () => {
      if (!layout || state !== 'board') return;
      state = 'flipping';
      const { cols, rows, end } = layout;
      // each cell starts in its turn of a wave across the board, spins, then lands — the
      // message's cells a beat after the rest, so it is the last thing to settle
      const startAt = (r: number, c: number) => Math.round((c / cols) * 8 + (r / rows) * 4);
      const landAt = end.map((row, r) =>
        // …and the amount last of all, one cell after another, left to right
        row.map((cell, c) =>
          cell.tone === 'hero'
            ? 8 + SPIN + 4 + 10 + c * 2
            : startAt(r, c) + SPIN + Math.floor(Math.random() * 5) + (cell.tone === 'lit' ? 6 : 0),
        ),
      );
      let tick = 0;
      timer = window.setInterval(() => {
        tick++;
        let done = true;
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const land = landAt[r][c];
            if (tick > land) continue;
            if (tick === land) {
              paint(cells[r][c], end[r][c].char, end[r][c].tone);
              continue;
            }
            done = false;
            if (tick >= startAt(r, c)) {
              paint(cells[r][c], SPINNING[Math.floor(Math.random() * SPINNING.length)], 'board');
            }
          }
        }
        if (done) {
          window.clearInterval(timer);
          state = 'done';
        }
      }, TICK_MS);
    };

    build();
    const resize = new ResizeObserver(() => {
      if (state !== 'flipping') build();
    });
    resize.observe(frame);
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        // a beat of the ordinary board first, then the flip
        delay = window.setTimeout(flip, 800);
        io.disconnect();
      },
      { threshold: 0.5 },
    );
    io.observe(section);

    return () => {
      window.clearTimeout(delay);
      window.clearInterval(timer);
      resize.disconnect();
      io.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      aria-label="Premios de 8.000 dólares en efectivo y pasajes a San Francisco"
      className="theme-ph26 selection-on-light relative flex h-svh w-full flex-col overflow-hidden bg-background p-3 text-primary md:p-5"
    >
      <div ref={frameRef} className="relative min-h-0 flex-1">
        <div
          ref={boardRef}
          aria-hidden="true"
          className="prize-board grid w-full gap-[3px] font-title uppercase leading-none md:gap-1"
        />
      </div>

      {/* down into the next section, which says where */}
      <span
        aria-hidden="true"
        className="-translate-x-1/2 pointer-events-none absolute bottom-2 left-1/2 font-mono text-xl leading-none motion-safe:animate-bounce md:bottom-4"
      >
        ↓
      </span>
    </section>
  );
}
